// =============================================================================
// features/sales/campaigns/detail/components/ImpactConfirmDialog.tsx —
// Confirmação de impacto ANTES de uma ação com efeito imediato numa
// campanha `active` (doc 26 §6.7: "Alterações em campanha ativa devem
// mostrar impacto antes da confirmação"). Dois usos, mesmo componente
// (norma: não duplicar um segundo diálogo de confirmação parecido):
//
//   1. Edição na aba Configuração — `changes` lista os campos alterados
//      (antes → depois), um DIFF do que o próprio gestor acabou de digitar
//      no formulário, nunca um número de negócio calculado.
//   2. Retomar uma campanha pausada (PauseResumeButton) — `message` (texto
//      livre, uma frase) no lugar do diff, já que não há "campo alterado":
//      o impacto é a distribuição de contatos recomeçando imediatamente.
//      Pausar NÃO usa este diálogo — é a ação seguras (interrompe, não
//      inicia), confirmação ali seria atrito sem ganho.
//
// `changes`/`message` são mutuamente exclusivos — o chamador informa um ou
// outro; título/descrição/rótulos têm default para o caso 1 (edição) e são
// sobrescritos explicitamente pelo caso 2 (retomar).
// =============================================================================

import * as React from 'react';

import { Button } from '../../../../../components/ui/Button';
import { useFocusTrap } from '../../../components/useFocusTrap';

export interface ImpactChange {
  label: string;
  before: string;
  after: string;
}

export interface ImpactConfirmDialogProps {
  campaignName: string;
  /** Diff campo a campo (edição da aba Configuração). */
  changes?: ImpactChange[];
  /** Frase única descrevendo o impacto — usada quando não há diff (ex.: retomar). */
  message?: string;
  /** Default: `Confirmar mudança em "<nome>"?` */
  title?: string;
  /** Default: aviso de campanha ativa/mudança em andamento. */
  description?: string;
  /** Default: "Confirmar mudança". */
  confirmLabel?: string;
  /** Default: "Salvando…". */
  pendingLabel?: string;
  /** Default: "Não foi possível salvar". */
  errorTitle?: string;
  onClose: () => void;
  onConfirm: () => Promise<{ ok: true } | { ok: false; message: string }>;
}

export function ImpactConfirmDialog({
  campaignName,
  changes = [],
  message,
  title,
  description = 'Esta campanha está ativa — a mudança afeta o trabalho em andamento imediatamente.',
  confirmLabel = 'Confirmar mudança',
  pendingLabel = 'Salvando…',
  errorTitle = 'Não foi possível salvar',
  onClose,
  onConfirm,
}: ImpactConfirmDialogProps): React.JSX.Element {
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [isPending, setIsPending] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const handleEscape = React.useCallback(() => {
    if (!isPending) onClose();
  }, [isPending, onClose]);

  useFocusTrap({ containerRef: panelRef, active: true, onEscape: handleEscape });

  async function handleConfirm(): Promise<void> {
    setIsPending(true);
    setSubmitError(null);
    const result = await onConfirm();
    setIsPending(false);
    if (!result.ok) {
      setSubmitError(result.message);
      return;
    }
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'color-mix(in srgb, var(--text) 60%, transparent)' }}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleEscape();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="impact-confirm-title"
        className="w-full max-w-md rounded-md flex flex-col overflow-hidden"
        style={{
          background: 'var(--bg-elev-1)',
          boxShadow: 'var(--elev-5)',
          border: '1px solid var(--border)',
          animation: 'fade-up 200ms var(--ease-out) both',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex flex-col gap-1 px-6 py-5"
          style={{ borderBottom: '1px solid var(--border)' }}
        >
          <h2
            id="impact-confirm-title"
            className="font-display font-bold text-ink"
            style={{ fontSize: 'var(--text-xl)', letterSpacing: '-0.03em' }}
          >
            {title ?? `Confirmar mudança em “${campaignName}”?`}
          </h2>
          <p className="font-sans text-sm text-ink-3">{description}</p>
        </div>

        <div className="flex flex-col gap-4 px-6 py-5">
          {changes.length > 0 ? (
            <ul className="flex flex-col gap-2 font-sans text-sm text-ink-2">
              {changes.map((c) => (
                <li key={c.label}>
                  <strong className="text-ink">{c.label}</strong>: {c.before} → {c.after}
                </li>
              ))}
            </ul>
          ) : message ? (
            <p className="font-sans text-sm text-ink-2">{message}</p>
          ) : null}

          {submitError && (
            <div
              role="alert"
              className="rounded-sm px-4 py-3 border-l-[3px]"
              style={{ borderColor: 'var(--danger)', background: 'var(--danger-bg)' }}
            >
              <p className="font-sans font-semibold text-sm text-danger">{errorTitle}</p>
              <p className="font-sans text-xs text-ink-2 mt-0.5">{submitError}</p>
            </div>
          )}

          <div
            className="flex flex-col gap-2.5 pt-1 mt-1"
            style={{ borderTop: '1px solid var(--border)' }}
          >
            <Button
              type="button"
              variant="secondary"
              disabled={isPending}
              className="w-full justify-center"
              onClick={() => void handleConfirm()}
            >
              {isPending ? pendingLabel : submitError ? 'Tentar novamente' : confirmLabel}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={isPending}
              className="w-full justify-center"
            >
              Cancelar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
